import React from 'react';

import { CircularProgress, useTheme } from '@mui/material';
import { CircleAlert } from 'lucide-react';
import { EmptyState } from 'src/components/EmptyState';
import { makeStyles } from 'tss-react/mui';

import { SkillBackButton } from './skill-editor-toolbar';

const useStyles = makeStyles()(theme => ({
  body: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    // Sits a little above center, where the eye lands on an empty page.
    paddingBottom: 80,
  },
  emptyState: { paddingTop: 0 },
  loading: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
  },
  root: {
    '& .MuiButton-root': { textTransform: 'none' },
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
  subtitle: { color: theme.palette.text.secondary, fontSize: 14 },
  toolbar: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    minHeight: 56,
    padding: '0 24px',
  },
}));

export const SkillEditorLoading = React.memo(function SkillEditorLoading() {
  const { classes } = useStyles();
  return (
    <div className={classes.loading}>
      <CircularProgress size={28} />
    </div>
  );
});

/** The skill could not be shown: it failed to load (with Retry), or it is not available. */
export const SkillEditorLoadError = React.memo(function SkillEditorLoadError({
  failed,
  onBack,
  onRetry,
  retrying,
}: {
  failed: boolean;
  onBack: () => void;
  onRetry: () => void;
  retrying: boolean;
}) {
  const { classes } = useStyles();
  const theme = useTheme();
  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <SkillBackButton onBack={onBack} />
      </div>
      <div className={classes.body}>
        <EmptyState
          buttonProps={{ disabled: retrying, loading: retrying, onClick: onRetry }}
          buttonText={failed ? 'Retry' : undefined}
          className={classes.emptyState}
          icon={<CircleAlert color={theme.palette.error.main} size={32} />}
          subtitle={
            failed
              ? 'Something went wrong. Try again in a moment.'
              : 'It may have been deleted, or you no longer have access to it.'
          }
          subtitleClassName={classes.subtitle}
          title={failed ? 'Could not load this skill' : 'Skill unavailable'}
        />
      </div>
    </div>
  );
});
